import { Box, CircularProgress, Typography } from '@mui/material';
import React, { Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { deleteExercise, loadExerciseByIndex, updateExercise } from '../../actions/exercises';
import { ExerciseRecord, ExerciseSearchItem } from '../../api/exercises.interface';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import DeleteExerciseDialog from './DeleteExerciseDialog';
import ExerciseForm from './ExerciseForm';
import { ExerciseFormErrors, ExerciseFormValues } from './exercise.types';
import {
    createEmptyExerciseFormValues,
    createExerciseFormValues,
    createExercisePayload,
    findDuplicateTagError,
    findExerciseByIndex,
    hasUnsavedExerciseChanges,
    validateExerciseForm,
} from './exercise.utils';
import UnsavedChangesDialog from './UnsavedChangesDialog';

interface ComponentProps {}

interface ReduxProps {
    deletingTag?: number;
    items: ExerciseSearchItem[];
    loadingItem: boolean;
    savingItem: boolean;
    selectedIndex?: number;
    selectedItem?: ExerciseRecord;
}

interface ReduxActionProps {
    deleteExercise: (tag: number, onSuccess?: () => void) => Promise<{ success: boolean }>;
    loadExerciseByIndex: (index: number) => void;
    updateExercise: (index: number, exercise: ExerciseRecord) => Promise<{ message?: string; success: boolean }>;
}

interface State {
    errors: ExerciseFormErrors;
    hydratedIndex?: number;
    initialValues: ExerciseFormValues;
    showDeleteDialog: boolean;
    showUnsavedChangesDialog: boolean;
    values: ExerciseFormValues;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class EditExercisePage extends Component<Props, State> {
    state: State = {
        errors: {},
        initialValues: createEmptyExerciseFormValues(),
        showDeleteDialog: false,
        showUnsavedChangesDialog: false,
        values: createEmptyExerciseFormValues(),
    };

    componentDidMount() {
        this.loadExerciseForRoute();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.params.index !== this.props.params.index) {
            this.loadExerciseForRoute();
            return;
        }

        const routeIndex = this.getRouteIndex();

        if (
            this.props.selectedItem
            && this.props.selectedIndex === routeIndex
            && this.state.hydratedIndex !== routeIndex
        ) {
            const nextValues = createExerciseFormValues(this.props.selectedItem);

            this.setState({
                errors: {},
                hydratedIndex: routeIndex,
                initialValues: nextValues,
                values: nextValues,
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    }

    onBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!this.isDirty()) {
            return undefined;
        }

        event.preventDefault();
        event.returnValue = '';
        return '';
    };

    onCancel = () => {
        if (!this.isDirty()) {
            this.props.navigate('/exercises');
            return;
        }

        this.setState({ showUnsavedChangesDialog: true });
    };

    onCloseDeleteDialog = () => {
        this.setState({ showDeleteDialog: false });
    };

    onCloseUnsavedChangesDialog = () => {
        this.setState({ showUnsavedChangesDialog: false });
    };

    onConfirmDelete = async () => {
        if (!this.props.selectedItem) {
            return;
        }

        await this.props.deleteExercise(this.props.selectedItem.tag, () => {
            this.props.navigate('/exercises');
        });
        this.setState({ showDeleteDialog: false });
    };

    onConfirmDiscardChanges = () => {
        this.setState({ showUnsavedChangesDialog: false });
        this.props.navigate('/exercises');
    };

    onFieldChanged = (name: keyof ExerciseFormValues, value: string) => {
        this.setState((currentState) => ({
            errors: {
                ...currentState.errors,
                [name]: undefined,
            },
            values: {
                ...currentState.values,
                [name]: value,
            },
        }));
    };

    onOpenDeleteDialog = () => {
        this.setState({ showDeleteDialog: true });
    };

    onSubmit = async () => {
        const routeIndex = this.getRouteIndex();

        if (routeIndex === undefined) {
            return;
        }

        const validationErrors = validateExerciseForm(this.state.values);

        if (Object.keys(validationErrors).length) {
            this.setState({ errors: validationErrors });
            return;
        }

        const result = await this.props.updateExercise(routeIndex, createExercisePayload(this.state.values));
        const duplicateTagError = findDuplicateTagError(result.message);

        if (duplicateTagError) {
            this.setState((currentState) => ({
                errors: {
                    ...currentState.errors,
                    tag: duplicateTagError,
                },
            }));
            return;
        }

        if (result.success) {
            this.setState((currentState) => ({
                initialValues: currentState.values,
            }));
        }
    };

    getRouteIndex() {
        const parsedIndex = Number(this.props.params.index);

        if (!Number.isInteger(parsedIndex) || parsedIndex < 0) {
            return undefined;
        }

        return parsedIndex;
    }

    getSelectedExerciseForDisplay() {
        const routeIndex = this.getRouteIndex();

        if (routeIndex === undefined) {
            return undefined;
        }

        if (this.props.selectedIndex === routeIndex && this.props.selectedItem) {
            return {
                ...this.props.selectedItem,
                index: routeIndex,
            };
        }

        return findExerciseByIndex(this.props.items, routeIndex);
    }

    isDirty() {
        return hasUnsavedExerciseChanges(this.state.initialValues, this.state.values);
    }

    loadExerciseForRoute() {
        const routeIndex = this.getRouteIndex();

        if (routeIndex === undefined) {
            return;
        }

        this.setState({
            errors: {},
            hydratedIndex: undefined,
            initialValues: createEmptyExerciseFormValues(),
            values: createEmptyExerciseFormValues(),
        });
        this.props.loadExerciseByIndex(routeIndex);
    }

    render() {
        const { deletingTag, loadingItem, savingItem } = this.props;
        const { errors, showDeleteDialog, showUnsavedChangesDialog, values } = this.state;
        const selectedExercise = this.getSelectedExerciseForDisplay();
        const routeIndex = this.getRouteIndex();

        if (routeIndex === undefined) {
            return <Typography variant="h6">Exercise not found</Typography>;
        }

        if (loadingItem && this.state.hydratedIndex !== routeIndex) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!selectedExercise && this.state.hydratedIndex !== routeIndex) {
            return <Typography variant="h6">Exercise not found</Typography>;
        }

        return (
            <Box maxWidth={1100}>
                <ExerciseForm
                    currentIndex={routeIndex}
                    currentTag={values.tag}
                    errors={errors}
                    onCancel={this.onCancel}
                    onDelete={this.onOpenDeleteDialog}
                    onFieldChanged={this.onFieldChanged}
                    onSubmit={this.onSubmit}
                    savingItem={savingItem}
                    showDeleteButton
                    submitLabel="Save Changes"
                    title="Edit Exercise"
                    values={values}
                />
                <DeleteExerciseDialog
                    deletingTag={deletingTag}
                    exercise={selectedExercise}
                    onCancel={this.onCloseDeleteDialog}
                    onConfirm={this.onConfirmDelete}
                    visible={showDeleteDialog}
                />
                <UnsavedChangesDialog onCancel={this.onCloseUnsavedChangesDialog} onConfirm={this.onConfirmDiscardChanges} visible={showUnsavedChangesDialog} />
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ exercises }: ReduxState) => ({
    deletingTag: exercises.deletingTag,
    items: exercises.items,
    loadingItem: exercises.loadingItem,
    savingItem: exercises.savingItem,
    selectedIndex: exercises.selectedIndex,
    selectedItem: exercises.selectedItem,
});

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, {
    deleteExercise,
    loadExerciseByIndex,
    updateExercise,
})(withRouter(EditExercisePage));